import {
  Trash2,
  BadgeCheck,
  Ban,
  Info,
  ChevronDown,
  Users,
  Pencil,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { updateJobStatus, deleteJob } from "../../../services/jobsServices";
import DeleteMemberModal from "../members/DeleteMemberModal";
import Cookies from "js-cookie";

const STATUS_OPTIONS = ["active", "closed"];

const statusIcons = {
  active: <BadgeCheck size={14} className="text-green" />,
  closed: <Ban size={14} className="text-red" />,
};

const statusStyles = {
  active: "bg-green text-white",
  closed: "bg-red text-white",
};

const StatusDropdown = ({ currentStatus, onChange, canEdit }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (status) => {
    setOpen(false);
    if (status !== currentStatus) {
      onChange(status);
    }
  };

  return (
    <div className="relative inline-block">
      <div
        onClick={() => canEdit && setOpen((prev) => !prev)}
        className={`text-xs font-semibold rounded-full px-2 py-1 cursor-pointer flex items-center justify-center gap-1 ${
          statusStyles[currentStatus]
        } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span className="capitalize">{currentStatus}</span>
        {canEdit && <ChevronDown size={12} />}
      </div>

      {open && canEdit && (
        <div className="absolute z-10 mt-1 bg-white shadow-md rounded border w-32 flex flex-col">
          {STATUS_OPTIONS.map((status) => (
            <div
              key={status}
              onClick={() => handleSelect(status)}
              className={`px-2 py-[6px] text-xs cursor-pointer flex items-center gap-2 hover:bg-gray-100 ${
                currentStatus === status ? "font-bold" : ""
              }`}
            >
              {statusIcons[status]}
              <span className="capitalize py-1">{status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobListTable = ({ jobs, refetch, startIndex, permissionsData }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const role = Cookies.get("role");

  let canEdit = false;
  let canDelete = false;
  let canViewApplicants = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
    canViewApplicants = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "Jobs"
    );
    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
    canViewApplicants = !!modulePermissions?.view;
  }

  const handleStatusUpdate = async (id, newStatus) => {
    if (!canEdit) return;
    try {
      await updateJobStatus(id, { status: newStatus });
      toast.success("Job status updated");
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmDelete = (job) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteJob(selectedJob._id);
      toast.success("Job deleted");
      setShowDeleteModal(false);
      setSelectedJob(null);
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleViewApplicants = (jobId, jobTitle) => {
    if (!canViewApplicants) return;
    const encodedJobId = encodeURIComponent(jobId);
    const encodedTitle = encodeURIComponent(jobTitle);

    window.location.href = `/admin/jobs/applied?jobId=${encodedJobId}&title=${encodedTitle}`;
  };

  const goEdit = (jobId) => {
    if (!canEdit) return;
    window.location.href = `/admin/jobs/edit-jobs?jobId=${jobId}`;
  };

  return (
    <div className="bg-white rounded-xl overflow-auto shadow-sm min-h-[300px]">
      <table className="w-full text-sm border border-gray-300 table-fixed">
        <thead className="bg-[#4F22D8] text-white">
          <tr>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-16">
              No
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-40">
              Title
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-24">
              Experience
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-20">
              Location
            </th>
            <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-20">
              Status
            </th>
            {(canEdit || canDelete || canViewApplicants) && (
              <th className="py-3 px-2 text-left font-bold border-r border-gray-400 w-40">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center py-6 text-gray-500">
                No jobs found.
              </td>
            </tr>
          ) : (
            jobs.map((job, idx) => (
              <tr
                key={job._id}
                className="border-t border-gray-200 hover:bg-gray-50"
              >
                <td className="px-3 py-2 border-r border-gray-300 w-16">
                  {startIndex + idx + 1}
                </td>
                <td className="px-3 py-2 border-r border-gray-300 w-48">
                  <div
                    className="text-sm leading-tight overflow-hidden"
                    title={job.title}
                  >
                    {job.title}
                  </div>
                </td>
                <td className="px-3 py-2 border-r border-gray-300 w-24">
                  {job.experience} yrs
                </td>
                <td className="px-3 py-2 border-r border-gray-300 w-32">
                  <div className="truncate" title={job.location}>
                    {job.location}
                  </div>
                </td>
                <td className="px-3 py-2 border-r border-gray-300 w-32">
                  <div className="flex items-center gap-2">
                    <StatusDropdown
                      currentStatus={job.status}
                      onChange={(newStatus) =>
                        handleStatusUpdate(job._id, newStatus)
                      }
                      canEdit={canEdit}
                    />
                    {canEdit && (
                      <div className="relative group">
                        <Info
                          size={14}
                          className="text-gray-400 hover:text-gray-600"
                        />
                        <div className="absolute z-20 hidden group-hover:block w-max bg-black text-white text-xs px-1.5 py-0.5 rounded top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-nowrap">
                          Please update status
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                {(canEdit || canDelete || canViewApplicants) && (
                  <td className="px-3 py-2 border-r border-gray-300 w-40">
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <button
                          onClick={() => goEdit(job._id)}
                          className="text-green font-semibold text-xs flex items-center gap-1"
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                      )}
                      {canViewApplicants && (
                        <button
                          onClick={() =>
                            handleViewApplicants(job._id, job.title)
                          }
                          className="text-[#4F22D8] font-semibold text-xs flex items-center gap-1"
                        >
                          <Users size={14} /> View Applicants
                        </button>
                      )}
                      {canEdit && canDelete && (
                        <div className="w-px h-4 bg-gray-300" />
                      )}
                      {canDelete && (
                        <button
                          onClick={() => confirmDelete(job)}
                          className="text-[#D40404] font-semibold text-xs flex items-center gap-1"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {selectedJob && (
        <DeleteMemberModal
          isOpen={showDeleteModal}
          onRequestClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          itemName={`${selectedJob.title} `}
          itemType="job"
        />
      )}
    </div>
  );
};

export default JobListTable;
